import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { ButtonComponent } from '@shared/components/button/button.component';
import {
  ReasonModalData,
  ReasonModalModalComponent,
  ReasonModalResponse,
} from '@shared/components/modals/reason-modal/reason-modal.component';
import { TooltipDirective } from '@shared/directives/tooltip.directive';
import {
  ApprovalType,
  EntityType,
  InvoiceStatus,
  PermissionType,
} from '@shared/services/gql.service';

import { InvoiceBannerButtonDirective } from './invoice-detail-banner-button.directive';
import { invoiceDisabledTooltip } from '../../invoices.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [
    {
      directive: InvoiceBannerButtonDirective,
      inputs: ['invoice'],
    },
  ],
  imports: [ButtonComponent, TooltipDirective],
  selector: 'aux-invoice-detail-status-banner-approve-button',
  template: `
    <aux-button
      [auxTooltip]="tooltip()"
      [disabled]="disabled()"
      [onClick]="onApprove"
      icon="Check"
      variant="success"
      label="Approve Invoice"
    />
  `,
})
export class InvoiceDetailApproveBtnComponent {
  ctx = inject(InvoiceBannerButtonDirective);

  hasApprovePermission = this.ctx.authService.$isAuthorized({
    permissions: [PermissionType.PERMISSION_APPROVE_INVOICE],
  });

  disabled = computed(() => {
    return (
      !this.hasApprovePermission() ||
      this.ctx.buttonDisabled() ||
      this.ctx.isAccrualPeriodInClosedMonth()
    );
  });
  tooltip = computed(() => {
    if (!this.hasApprovePermission()) {
      return this.ctx.dontHavePermission;
    }

    if (this.ctx.buttonDisabled()) {
      return this.ctx.invoiceLockTooltip();
    }

    if (this.ctx.isAccrualPeriodInClosedMonth()) {
      return invoiceDisabledTooltip;
    }

    return '';
  });

  onApprove = async () => {
    const { gqlService, invoiceService, overlayService, userTaskService } = this.ctx;
    const invoice = this.ctx.invoice();

    const resp = overlayService.openPopup<
      ReasonModalData,
      ReasonModalResponse,
      ReasonModalModalComponent
    >({
      content: ReasonModalModalComponent,
      data: {
        message: `Are you sure you want to approve Invoice ${invoice.invoice_no}?`,
      },
      settings: {
        header: 'Approve Invoice?',
        primaryButton: {
          action: (instance: null | ReasonModalModalComponent) => instance?.save(),
          label: 'Approve',
        },
      },
    });

    const event = await firstValueFrom(resp.afterClosed$);

    if (!event.data) {
      return;
    }

    const { errors: approveErrors, success: approveSuccess } = await firstValueFrom(
      gqlService.approveRule$({
        activity_details: '{}',
        approval_type: ApprovalType.APPROVAL_INVOICE,
        approved: true,
        comments: '',
        entity_id: invoice.id,
        entity_type: EntityType.INVOICE,
        permission: 'PERMISSION_APPROVE_INVOICE',
      })
    );

    if (!approveSuccess) {
      overlayService.error(approveErrors);

      return;
    }

    await invoiceService.update({
      ...invoice,
      invoice_status: InvoiceStatus.STATUS_APPROVED,
    });
    await userTaskService.triggerUserTaskList$();
  };
}
